import { useTranslation } from 'react-i18next';

import * as St from './SinkInfoContent.styles';

import { IMAGE_COMING_SOON } from '~/core/constants';
import { useSink } from '~/core/hooks/useSink';
import { ApplianceModel, ApplianceType } from '~/core/models';
import {
  formatPriceUnitOrFree,
  getProductDescription,
  getSinkPrices,
  hasTrendingOrBestSelling,
  imgUrl,
  isBrowsedSink,
} from '~/core/utils';
import CardSaleImg from '~atoms/CardSaleImg';

type SinkInfoContentProps = {
  sink: ApplianceModel;
  showChipMinimizer: boolean;
};

const SinkInfoContent = ({ sink, showChipMinimizer }: SinkInfoContentProps) => {
  const { t } = useTranslation();
  const mainImage = imgUrl(sink.beautyShotImageUrl || sink.imageUrl, 300);
  const { getSinkCutoutLaborProductData } = useSink();
  const trendLabel = hasTrendingOrBestSelling(sink.features);

  const { cutoutLaborProduct, cutoutLaborProductName } =
    getSinkCutoutLaborProductData(sink);

  const sinkHasWidth = !!sink.width;

  const {
    cutoutLaborPrice,
    sinkAndCutoutLaborPriceWithDiscount,
    sinkPrice,
    sinkPriceWithDiscount,
  } = getSinkPrices(sink, cutoutLaborProduct);

  /**
   * if the sink is customer supplied we want a "free" value to be displayed as "Customer Supplied"
   * Otherwise, we want to default "Free" text
   * I put this here just so I could document this ugliness
   */
  const freeValueDisplayOption =
    sink.applianceType === ApplianceType.SINK_LABOR
      ? t('SCROLLABLE_AREA.SINK.CUSTOMER_SUPPLIED_FREE')
      : undefined;

  return (
    <St.Container data-has-chip-minimizer={showChipMinimizer}>
      {trendLabel && (
        <St.TrendBadge>{t(trendLabel).toUpperCase()}</St.TrendBadge>
      )}

      <St.Title>{getProductDescription(sink)}</St.Title>
      <St.InsideContainer>
        <St.MainImage>
          <img src={mainImage} alt={sink.name} />
        </St.MainImage>
        <St.Description>
          <St.ModalDescription>
            {isBrowsedSink(sink)
              ? t('SCROLLABLE_AREA.SINK.ADDITIONAL_VALUES_INFORMATION')
              : t(
                  `SCROLLABLE_AREA.SINK.ADDITIONAL_VALUES_INFORMATION_CUSTOMER_SUPPLIED`
                )}
          </St.ModalDescription>
          <St.PriceContainer>
            <St.PriceList>
              <St.DiscountContainer>
                {sink.discountPercentage && <CardSaleImg />}
                <St.PriceContainer>
                  <St.PriceInfo>
                    <St.ItalicPriceLabel>Sink</St.ItalicPriceLabel>
                    <St.PriceValue>
                      {formatPriceUnitOrFree(
                        sinkPriceWithDiscount,
                        null,
                        true,
                        freeValueDisplayOption
                      )}
                    </St.PriceValue>
                  </St.PriceInfo>
                  {sink.discountPercentage && (
                    <St.PriceInfo>
                      <St.DiscountLabel>
                        {sink.discountPercentage}% Off
                      </St.DiscountLabel>
                      <St.DiscountPrice>
                        {formatPriceUnitOrFree(sinkPrice, null, true)}
                      </St.DiscountPrice>
                    </St.PriceInfo>
                  )}
                </St.PriceContainer>
              </St.DiscountContainer>
              <St.PriceInfo>
                <St.ItalicPriceLabel>
                  {cutoutLaborProductName}
                </St.ItalicPriceLabel>
                <St.PriceValue>
                  {formatPriceUnitOrFree(cutoutLaborPrice, null, true)}
                </St.PriceValue>
              </St.PriceInfo>
            </St.PriceList>
            <St.TotalPrice>
              Total:{' '}
              <St.PriceValue>
                {formatPriceUnitOrFree(
                  sinkAndCutoutLaborPriceWithDiscount,
                  null,
                  true
                )}
              </St.PriceValue>
            </St.TotalPrice>
          </St.PriceContainer>
        </St.Description>
      </St.InsideContainer>
      <St.InsideContainer>
        <St.ImageContainer>
          <St.ImageHeader>Sink</St.ImageHeader>{' '}
          <St.InsideContainer className={sinkHasWidth ? 'sink' : ''}>
            <St.MiniatureMainImage src={mainImage} alt={sink.name} />
          </St.InsideContainer>
        </St.ImageContainer>
        <St.ImageContainer>
          <St.ImageHeader>Sink Dimensions</St.ImageHeader>
          <St.InsideContainer
            className={`dimensions ${sinkHasWidth ? '' : 'no-width'}`}
          >
            <St.TempImage src={IMAGE_COMING_SOON} alt="Coming soon" />
            <St.TempImage src={IMAGE_COMING_SOON} alt="Coming soon" />
          </St.InsideContainer>
        </St.ImageContainer>
        {sinkHasWidth && (
          <St.ImageContainer>
            <St.ImageHeader>
              Min. {sink.width}
              {"''"} cabinet width
            </St.ImageHeader>
            <St.TempImage src={IMAGE_COMING_SOON} alt="Coming soon" />
          </St.ImageContainer>
        )}
      </St.InsideContainer>
    </St.Container>
  );
};

export default SinkInfoContent;
